import React, { useState } from 'react';
import { Wand2 } from 'lucide-react';

type TeamStyle = 'funny' | 'clever' | 'intimidating' | 'player-based';

const funnyPrefixes = ['Touchdown', 'Gridiron', 'Fantasy', 'Pigskin', 'Draft', 'League', 'Field', 'Game'];
const cleverPrefixes = ['Elite', 'Pro', 'Prime', 'Super', 'Ultra', 'Mega', 'Epic', 'Legend'];
const intimidatingPrefixes = ['Savage', 'Beast', 'Brutal', 'Fierce', 'Mighty', 'Warrior', 'Titan', 'Thunder'];
const playerPrefixes = ['Brady', 'Mahomes', 'Rodgers', 'Wilson', 'Jackson', 'Allen', 'Kelce', 'Donald'];

const funnySuffixes = ['Wizards', 'Ninjas', 'Pandas', 'Unicorns', 'Llamas', 'Pickles', 'Tacos', 'Memes'];
const cleverSuffixes = ['Dynasty', 'Empire', 'Legacy', 'Legends', 'Masters', 'Elite', 'Kings', 'Champions'];
const intimidatingSuffixes = ['Destroyers', 'Crushers', 'Dominators', 'Warlords', 'Assassins', 'Predators', 'Beasts', 'Titans'];
const playerSuffixes = ['Nation', 'Army', 'Squad', 'Crew', 'Team', 'Gang', 'Posse', 'Alliance'];

const generateNames = (style: TeamStyle, keywords: string): string[] => {
  let prefixes: string[];
  let suffixes: string[];

  switch (style) {
    case 'funny':
      prefixes = funnyPrefixes;
      suffixes = funnySuffixes;
      break;
    case 'clever':
      prefixes = cleverPrefixes;
      suffixes = cleverSuffixes;
      break;
    case 'intimidating':
      prefixes = intimidatingPrefixes;
      suffixes = intimidatingSuffixes;
      break;
    case 'player-based':
      prefixes = playerPrefixes;
      suffixes = playerSuffixes;
      break;
  }

  const names: string[] = [];
  for (let i = 0; i < 5; i++) {
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
    const keyword = keywords ? ` ${keywords} ` : ' ';
    names.push(`${prefix}${keyword}${suffix}`);
  }

  return names;
};

const FantasyFootballNameGenerator: React.FC = () => {
  const [style, setStyle] = useState<TeamStyle>('funny');
  const [keywords, setKeywords] = useState('');
  const [generatedNames, setGeneratedNames] = useState<string[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);

  const handleGenerate = () => {
    const names = generateNames(style, keywords);
    setGeneratedNames(names);
  };

  const toggleFavorite = (name: string) => {
    setFavorites(prev => 
      prev.includes(name) 
        ? prev.filter(n => n !== name)
        : [...prev, name]
    );
  };

  return (
    <section id="fantasy-football-generator" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="bg-green-100 text-green-800 px-4 py-1 rounded-full text-sm font-medium">
            Fantasy Football Team Names
          </span>
        </div>
        
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          Fantasy Football Team Name Generator
        </h2>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Team Style
              </label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value as TeamStyle)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 bg-white"
              >
                <option value="funny">Funny</option>
                <option value="clever">Clever</option>
                <option value="intimidating">Intimidating</option>
                <option value="player-based">Player-Based</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Keywords (Optional)
              </label>
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="E.g., Champion, Winner, MVP"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 bg-white"
              />
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={handleGenerate}
              className="bg-green-600 text-white py-4 px-8 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Wand2 className="w-5 h-5" />
              Generate Names
            </button>
          </div>

          {generatedNames.length > 0 && (
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                Generated Names:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {generatedNames.map((name, index) => (
                  <div
                    key={index}
                    className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between"
                  >
                    <span className="text-lg">{name}</span>
                    <button
                      onClick={() => toggleFavorite(name)}
                      className={`ml-4 p-2 rounded-full transition-colors ${
                        favorites.includes(name)
                          ? 'text-yellow-500 hover:text-yellow-600'
                          : 'text-gray-400 hover:text-gray-500'
                      }`}
                    >
                      ★
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 text-center text-sm text-gray-500">
            <p className="mb-2">Pro tip: Click the star icon to save your favorite names!</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FantasyFootballNameGenerator;